import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories } from '@/mocks/categories';
import { getFeaturedArticles, getLatestArticles } from '@/mocks/articles';

const HeroSection = () => (
  <section className="relative w-full h-[480px] md:h-[620px] overflow-hidden">
    <div className="absolute inset-0">
      <img
        src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20technology%20workspace%20with%20multiple%20monitors%20displaying%20code%20on%20wooden%20desk%2C%20warm%20ambient%20lighting%20from%20desk%20lamps%2C%20indoor%20plants%2C%20abstract%20digital%20network%20visualization%20overlay%2C%20dark%20green%20and%20golden%20color%20palette%2C%20editorial%20photography%20style%20with%20cinematic%20atmosphere%2C%20soft%20bokeh%20background%2C%20texture%20of%20wood%20grain%20and%20smooth%20metal%20surfaces%2C%20intellectual%20and%20creative%20vibe&width=1800&height=900&seq=hero-tech-community&orientation=landscape"
        alt="草榴社区技术讨论区 - 专业技术交流平台"
        className="w-full h-full object-cover object-top"
        title="草榴社区技术讨论区 - 技术交流与资源分享平台"
      />
    </div>
    <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="w-full max-w-4xl mx-auto px-4 md:px-6 text-center">
        <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-5 leading-tight">
          草榴社区技术讨论区
        </h1>
        <p className="text-base md:text-lg text-white/85 max-w-2xl mx-auto mb-8 leading-relaxed">
          汇聚编程开发、网络安全、人工智能等领域的技术精英，打造最纯粹的技术交流与资源分享平台，助力每一位技术人员持续成长
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/category/programming"
            className="px-8 py-3 bg-primary-500 text-white rounded-md text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            浏览技术文章
          </Link>
          <Link
            to="/about"
            className="px-8 py-3 bg-white/15 backdrop-blur-sm text-white border border-white/30 rounded-md text-sm font-medium hover:bg-white/25 transition-colors whitespace-nowrap"
          >
            了解我们
          </Link>
        </div>
        <div className="flex items-center justify-center gap-6 mt-10">
          <div className="text-center">
            <div className="text-2xl md:text-3xl font-bold text-white">500+</div>
            <div className="text-xs md:text-sm text-white/70 mt-1">技术文章</div>
          </div>
          <div className="w-px h-10 bg-white/20"></div>
          <div className="text-center">
            <div className="text-2xl md:text-3xl font-bold text-white">10K+</div>
            <div className="text-xs md:text-sm text-white/70 mt-1">社区成员</div>
          </div>
          <div className="w-px h-10 bg-white/20"></div>
          <div className="text-center">
            <div className="text-2xl md:text-3xl font-bold text-white">6</div>
            <div className="text-xs md:text-sm text-white/70 mt-1">技术领域</div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const CategoryGrid = () => (
  <section className="py-14 md:py-20 bg-background-50">
    <div className="max-w-7xl mx-auto px-4 md:px-6">
      <div className="text-center mb-10 md:mb-14">
        <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
          技术讨论分类
        </h2>
        <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
          六大核心技术领域，覆盖从编程入门到架构设计的全方位技术讨论
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
        {categories.map((cat) => (
          <Link
            key={cat.id}
            to={`/category/${cat.slug}`}
            className="group bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 hover:border-primary-300 hover:bg-background-100 transition-all duration-200"
          >
            <div className="w-11 h-11 md:w-12 md:h-12 rounded-lg bg-primary-100 flex items-center justify-center mb-4 group-hover:bg-primary-200 transition-colors">
              <i className={`${cat.icon} text-primary-600 text-xl`}></i>
            </div>
            <h3 className="text-base md:text-lg font-semibold text-foreground-950 mb-2 whitespace-nowrap">
              {cat.name}
            </h3>
            <p className="text-sm text-foreground-600 leading-relaxed mb-3 line-clamp-2">
              {cat.description}
            </p>
            <span className="text-xs text-foreground-500">
              {cat.articleCount} 篇文章
            </span>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

const FeaturedArticles = () => {
  const featured = getFeaturedArticles();
  return (
    <section className="py-14 md:py-20 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between mb-10 md:mb-14">
          <div>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
              热门技术文章
            </h2>
            <p className="text-sm text-foreground-600">社区最受欢迎的技术深度好文</p>
          </div>
          <Link
            to="/category/programming"
            className="hidden sm:flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors whitespace-nowrap"
          >
            查看全部 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {featured.map((article) => (
            <Link
              key={article.id}
              to={`/article/${article.id}`}
              className="group bg-background-50 rounded-lg border border-background-200/70 p-5 md:p-6 hover:border-primary-300 transition-all duration-200"
            >
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full whitespace-nowrap">
                  {categories.find((c) => c.slug === article.category)?.name || article.category}
                </span>
                <span className="text-xs text-foreground-500">{article.date}</span>
              </div>
              <h3 className="text-base md:text-lg font-semibold text-foreground-950 mb-2.5 group-hover:text-primary-600 transition-colors line-clamp-2">
                {article.title}
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4 line-clamp-2">
                {article.excerpt}
              </p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <img
                    src={article.authorAvatar}
                    alt={article.author}
                    className="w-7 h-7 rounded-full object-cover"
                    title={article.author}
                  />
                  <span className="text-xs text-foreground-600">{article.author}</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i> {article.views}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-heart-line"></i> {article.likes}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-chat-3-line"></i> {article.comments}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
        <div className="mt-6 text-center sm:hidden">
          <Link
            to="/category/programming"
            className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors whitespace-nowrap"
          >
            查看全部文章 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
};

const LatestDiscussions = () => {
  const latest = getLatestArticles(6);
  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between mb-10 md:mb-14">
          <div>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
              最新技术讨论
            </h2>
            <p className="text-sm text-foreground-600">实时更新社区最新技术文章和讨论</p>
          </div>
          <Link
            to="/category/programming"
            className="hidden sm:flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors whitespace-nowrap"
          >
            更多文章 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg divide-y divide-background-200/70">
          {latest.map((article, index) => (
            <Link
              key={article.id}
              to={`/article/${article.id}`}
              className="flex items-start gap-4 p-4 md:p-5 hover:bg-background-100 transition-colors group"
            >
              <div className="flex-shrink-0 w-8 h-8 md:w-9 md:h-9 rounded-md bg-primary-100 flex items-center justify-center">
                <span className="text-sm font-semibold text-primary-600">{index + 1}</span>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm md:text-base font-medium text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-1 mb-1.5">
                  {article.title}
                </h3>
                <div className="flex items-center gap-3 text-xs text-foreground-500 flex-wrap">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i> {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i> {article.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i> {article.views} 阅读
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-chat-3-line"></i> {article.comments} 评论
                  </span>
                </div>
              </div>
              <span className="flex-shrink-0 px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full hidden sm:block whitespace-nowrap">
                {categories.find((c) => c.slug === article.category)?.name || article.category}
              </span>
            </Link>
          ))}
        </div>
        <div className="mt-6 text-center sm:hidden">
          <Link
            to="/category/programming"
            className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors whitespace-nowrap"
          >
            更多文章 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
};

const CTASection = () => (
  <section className="py-14 md:py-20 bg-primary-500">
    <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
      <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
        加入草榴社区技术讨论区
      </h2>
      <p className="text-sm md:text-base text-white/85 max-w-xl mx-auto mb-8">
        与数千名技术爱好者一起，分享知识、交流经验、共同成长
      </p>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
        <Link
          to="/category/programming"
          className="px-8 py-3 bg-background-50 text-primary-600 rounded-md text-sm font-medium hover:bg-background-100 transition-colors whitespace-nowrap"
        >
          立即浏览文章
        </Link>
        <Link
          to="/about"
          className="px-8 py-3 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 transition-colors whitespace-nowrap"
        >
          了解更多
        </Link>
      </div>
    </div>
  </section>
);

const HomePage = () => {
  const webPageSchema = {
    '@type': 'WebPage',
    name: '草榴社区技术讨论区 - 首页',
    description: '技术爱好者专业交流平台，汇聚编程、网络安全、人工智能等技术话题',
    url: 'https://techcommunity.cn',
  };

  return (
    <>
      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebSite" />
      <SchemaOrg type="WebPage" data={webPageSchema} />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <HeroSection />
          <CategoryGrid />
          <FeaturedArticles />
          <LatestDiscussions />
          <CTASection />
        </main>
        <Footer />
      </div>
    </>
  );
};

export default HomePage;